import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { AuthModal } from "../ui/auth/AuthModal";

interface AuthModalContextType {
  show: (params?: {
    canClose: boolean;
    additionalBtn?: {
      label: string;
      onClick: () => void;
    };
  }) => Promise<void>;
  close: () => void;
}

const AuthModalContext = createContext<AuthModalContextType | undefined>(
  undefined
);

export function useAuthModal() {
  const ctx = useContext(AuthModalContext);
  if (!ctx) {
    throw new Error("useAuthModal must be used within an AuthModalProvider");
  }
  return ctx;
}

export function AuthModalProvider({ children }: { children: React.ReactNode }) {
  const [isShow, setIsShow] = useState(false);
  const [canClose, setCanClose] = useState(true);
  const [additionalBtn, setAdditionalBtn] = useState<{
    label: string;
    onClick: () => void;
  } | null>(null);
  const promiseRef = useRef<{ resolve: (value: void) => void } | null>(null);

  const show = useCallback(
    (params?: {
      canClose: boolean;
      additionalBtn?: {
        label: string;
        onClick: () => void;
      };
    }): Promise<void> => {
      setCanClose(params?.canClose ?? true);
      setAdditionalBtn(params?.additionalBtn ?? null);
      setIsShow(true);
      return new Promise<void>((resolve) => {
        promiseRef.current = { resolve };
      });
    },
    []
  );

  const close = useCallback(() => {
    setIsShow(false);
    if (promiseRef.current) {
      promiseRef.current.resolve();
      promiseRef.current = null;
    }
  }, []);

  const handleClose = () => {
    setIsShow(false);
    if (promiseRef.current) {
      promiseRef.current.resolve();
      promiseRef.current = null;
    }
  };

  return (
    <AuthModalContext.Provider value={{ show, close }}>
      {children}
      <AuthModal
        isShown={isShow}
        canClose={canClose}
        onClose={handleClose}
        additionalBtn={additionalBtn ?? undefined}
      />
    </AuthModalContext.Provider>
  );
}
